/**
 * AgentAIInbox
 *
 * Gives agents:
 *  - Real-time list of conversations waiting for a human (WAITING_AGENT)
 *  - Live WebSocket chat with customer once taken over
 *  - AI-suggested replies (one click to send)
 *  - Full history including what the AI said
 *  - Release back to AI button
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { connectConversationWS, connectAgentQueueWS } from '../services/aiWebSocket';

const API = '/api/ai';

// ── Types ─────────────────────────────────────────────────────────────────────

interface Conversation {
  id: string;
  customer_id: string;
  customer_name?: string;
  customer_email?: string;
  channel: string;
  status: string;
  created_at: string;
  metadata?: { target_queue_id?: number; target_queue_name?: string; [k: string]: any };
}

interface Message {
  id: string;
  sender_type: 'CUSTOMER' | 'AI' | 'AGENT' | 'SYSTEM';
  sender_id?: string;
  message: string;
  timestamp: string;
  confidence_score?: number;
  escalation_triggered?: boolean;
}

interface AgentAIInboxProps {
  user: any;
}

// ── Colours ───────────────────────────────────────────────────────────────────

const STATUS_PILL: Record<string, { bg: string; text: string; label: string }> = {
  WAITING_AGENT:  { bg: '#f59e0b22', text: '#f59e0b', label: 'Waiting' },
  AGENT_ASSIGNED: { bg: '#6366f122', text: '#6366f1', label: 'Active'  },
  AI_ACTIVE:      { bg: '#10b98122', text: '#10b981', label: 'AI'      },
  CLOSED:         { bg: '#64748b22', text: '#64748b', label: 'Closed'  },
};

const BUBBLE: Record<string, { bg: string; color: string; align: 'flex-start' | 'flex-end' }> = {
  CUSTOMER: { bg: '#e2e8f0', color: '#0f172a', align: 'flex-start' },
  AI:       { bg: '#ede9fe', color: '#3b0764', align: 'flex-end'   },
  AGENT:    { bg: '#d1fae5', color: '#052e16', align: 'flex-end'   },
  SYSTEM:   { bg: '#fef3c722', color: '#92400e', align: 'flex-start'},
};

const CHANNEL_ICON: Record<string, string> = {
  web_chat: '🌐', whatsapp: '💬', sms: '📱', email: '✉️', voice: '🎙️', mobile: '📲',
};

// ── Component ─────────────────────────────────────────────────────────────────

export default function AgentAIInbox({ user }: AgentAIInboxProps) {
  const token = localStorage.getItem('token');
  const auth  = { headers: { Authorization: `Bearer ${token}` } };

  // Conversation list
  const [waiting,  setWaiting]  = useState<Conversation[]>([]);
  const [myConvs,  setMyConvs]  = useState<Conversation[]>([]);
  const [tab,      setTab]      = useState<'waiting' | 'mine'>('waiting');

  // Active chat
  const [selected, setSelected] = useState<Conversation | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [input,    setInput]    = useState('');
  const [sending,  setSending]  = useState(false);
  const [typing,   setTyping]   = useState(false);
  const [aiSuggest, setAiSuggest] = useState('');
  const [loadingSuggest, setLoadingSuggest] = useState(false);
  const [actionError, setActionError] = useState<string>('');
  // Agent's queue memberships (fetched from main API)
  const [agentQueueIds, setAgentQueueIds] = useState<number[]>([]);
  const [agentQueues, setAgentQueues]     = useState<{id:number;display_name:string}[]>([]);
  const [queueFilter, setQueueFilter]     = useState<'all' | number>('all');

  // WebSocket refs
  const convWsRef      = useRef<any>(null);
  const queueWsRef     = useRef<any>(null);
  const bottomRef      = useRef<HTMLDivElement>(null);
  const typingTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // ── Load conversation lists ────────────────────────────────────────────────

  const loadWaiting = useCallback(async () => {
    const r = await axios.get(`${API}/conversations`, {
      params: { status: 'WAITING_AGENT', page_size: 50 }, ...auth,
    });
    setWaiting(r.data.items || []);
  }, []);

  const loadMine = useCallback(async () => {
    const r = await axios.get(`${API}/conversations`, {
      params: { status: 'AGENT_ASSIGNED', assigned_agent: user.id, page_size: 50 }, ...auth,
    });
    setMyConvs(r.data.items || []);
  }, [user.id]);

  // Load agent's queue memberships
  useEffect(() => {
    axios.get('/api/agent/my-queue-memberships', auth)
      .then((r: any) => { const qs = r.data || []; setAgentQueues(qs.map((m:any)=>({id:m.id,display_name:m.display_name||m.queue_name}))); setAgentQueueIds(qs.map((m:any)=>m.id)); })
      .catch(() => {});
  }, [user.id]);

  useEffect(() => {
    loadWaiting();
    loadMine();
    const t = setInterval(() => { loadWaiting(); loadMine(); }, 15_000);
    return () => clearInterval(t);
  }, [loadWaiting, loadMine]);

  // ── Agent queue WebSocket (escalation alerts) ──────────────────────────────

  useEffect(() => {
    if (!token) return;
    const ws = connectAgentQueueWS(token);
    queueWsRef.current = ws;

    ws.on('escalation', () => {
      loadWaiting();
      // Flash the tab
      setTab('waiting');
    });

    return () => ws.disconnect();
  }, [token, loadWaiting]);

  // ── Open a conversation ────────────────────────────────────────────────────

  const openConversation = async (conv: Conversation) => {
    convWsRef.current?.disconnect();
    setSelected(conv);
    setMessages([]);
    setInput('');
    setAiSuggest('');

    const r = await axios.get(`${API}/conversations/${conv.id}/messages`, auth);
    setMessages(r.data || []);
    setTimeout(() => bottomRef.current?.scrollIntoView({ behavior: 'smooth' }), 100);

    const ws = connectConversationWS(conv.id, token, 'agent', String(user.id));
    convWsRef.current = ws;

    ws.on('ai_message', (d) => {
      setMessages(m => [...m, {
        id: String(d.message_id), sender_type: 'AI',
        message: String(d.message), timestamp: String(d.timestamp),
        confidence_score: d.confidence as number,
      }]);
      setTyping(false);
      bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
    });

    ws.on('agent_message', (d) => {
      setMessages(m => [...m, {
        id: String(d.message_id), sender_type: 'AGENT',
        message: String(d.message), timestamp: String(d.timestamp),
      }]);
      bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
    });

    // Customer replies while agent is active
    ws.on('customer_message', (d) => {
      setMessages(m => [...m, {
        id: String(Date.now()), sender_type: 'CUSTOMER',
        message: String(d.message), timestamp: String(d.timestamp),
      }]);
      setTyping(false);
      bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
    });

    ws.on('typing', () => { setTyping(true); setTimeout(() => setTyping(false), 3000); });
    ws.on('returned_to_ai', () => { loadWaiting(); loadMine(); setSelected(null); });
  };

  useEffect(() => () => convWsRef.current?.disconnect(), []);

  // ── Takeover ───────────────────────────────────────────────────────────────

  const takeover = async (conv: Conversation) => {
    setActionError('');
    try {
      await axios.post(`${API}/agent/takeover`, { conversation_id: conv.id }, auth);
      await loadWaiting();
      await loadMine();
      const updated = { ...conv, status: 'AGENT_ASSIGNED' };
      setSelected(updated);
      await openConversation(updated);
    } catch (err: any) {
      const msg = err?.response?.data?.detail || err?.message || 'Takeover failed';
      setActionError(String(msg));
    }
  };

  // ── Send message ───────────────────────────────────────────────────────────

  const sendMessage = async () => {
    if (!selected || !input.trim()) return;
    const text = input;
    setInput('');
    setSending(true);
    setActionError('');
    try {
      await axios.post(`${API}/agent/message`, { conversation_id: selected.id, message: text }, auth);
    } catch (err: any) {
      setInput(text);
      const msg = err?.response?.data?.detail || err?.message || 'Send failed';
      setActionError(String(msg));
    } finally {
      setSending(false);
    }
  };

  // ── AI Suggest ─────────────────────────────────────────────────────────────

  const getAiSuggestion = async () => {
    if (!selected) return;
    setLoadingSuggest(true);
    try {
      const r = await axios.post(`${API}/agent/suggest`, { conversation_id: selected.id }, auth);
      setAiSuggest(r.data.suggestion || '');
    } catch {
      setAiSuggest('');
    } finally {
      setLoadingSuggest(false);
    }
  };

  // ── Release ────────────────────────────────────────────────────────────────

  const release = async () => {
    if (!selected) return;
    setActionError('');
    try {
      await axios.post(`${API}/agent/release`, { conversation_id: selected.id }, auth);
      await loadWaiting();
      await loadMine();
      setSelected(null);
    } catch (err: any) {
      const msg = err?.response?.data?.detail || err?.message || 'Release failed';
      setActionError(String(msg));
    }
  };

  // ── Close ──────────────────────────────────────────────────────────────────

  const closeConv = async () => {
    if (!selected) return;
    setActionError('');
    try {
      await axios.post(`${API}/conversations/${selected.id}/close`, {}, auth);
      await loadMine();
      setSelected(null);
    } catch (err: any) {
      const msg = err?.response?.data?.detail || err?.message || 'Close failed';
      setActionError(String(msg));
    }
  };

  // ── Render ─────────────────────────────────────────────────────────────────

  const listItems = tab === 'waiting' ? waiting : myConvs;

  return (
    <div style={{ display: 'flex', height: '100%', background: '#0f172a', color: '#e2e8f0', fontFamily: 'inherit' }}>

      {/* ── Left panel: conversation list ── */}
      <div style={{ width: 300, borderRight: '1px solid #1e293b', display: 'flex', flexDirection: 'column', flexShrink: 0 }}>

        {/* Tabs */}
        <div style={{ display: 'flex', borderBottom: '1px solid #1e293b' }}>
          {(['waiting', 'mine'] as const).map(t => (
            <button key={t} onClick={() => setTab(t)} style={{
              flex: 1, padding: '12px 8px', background: 'none', border: 'none', cursor: 'pointer',
              color: tab === t ? '#c4b5fd' : '#64748b',
              fontWeight: tab === t ? 700 : 400, fontSize: 13,
              borderBottom: tab === t ? '2px solid #8b5cf6' : '2px solid transparent',
              transition: 'color 0.15s',
            }}>
              {t === 'waiting' ? (
                <>Waiting {waiting.length > 0 && <span style={{ background: '#f59e0b', color: '#0f172a', borderRadius: 10, padding: '1px 6px', fontSize: 11, fontWeight: 800, marginLeft: 4 }}>{waiting.length}</span>}</>
              ) : (
                <>My Chats {myConvs.length > 0 && <span style={{ background: '#6366f1', color: '#fff', borderRadius: 10, padding: '1px 6px', fontSize: 11, fontWeight: 800, marginLeft: 4 }}>{myConvs.length}</span>}</>
              )}
            </button>
          ))}
        </div>

        {/* Queue filter */}
        {agentQueueIds.length > 0 && (
          <div style={{ padding: '6px 10px', borderBottom: '1px solid #1e293b', display: 'flex', gap: 4, flexWrap: 'wrap' }}>
            <button onClick={() => setQueueFilter('all')}
              style={{ fontSize: 11, padding: '3px 8px', borderRadius: 6, border: 'none', cursor: 'pointer',
                background: queueFilter === 'all' ? '#6366f1' : '#1e293b', color: queueFilter === 'all' ? '#fff' : '#94a3b8', fontWeight: 600 }}>
              All
            </button>
            {agentQueues.map(q => (
              <button key={q.id} onClick={() => setQueueFilter(q.id)}
                style={{ fontSize: 11, padding: '3px 8px', borderRadius: 6, border: 'none', cursor: 'pointer',
                  background: queueFilter === q.id ? '#6366f1' : '#1e293b', color: queueFilter === q.id ? '#fff' : '#94a3b8', fontWeight: 600 }}>
                {q.display_name}
              </button>
            ))}
          </div>
        )}

        {/* List */}
        <div style={{ flex: 1, overflowY: 'auto' }}>
          {listItems.length === 0 ? (
            <div style={{ padding: 28, textAlign: 'center', color: '#334155', fontSize: 13 }}>
              {tab === 'waiting' ? '✅ No conversations waiting' : 'No active chats'}
            </div>
          ) : (
            listItems
            .filter(conv => {
              if (queueFilter === 'all') return true;
              const qid = conv.metadata?.target_queue_id;
              return !qid || agentQueueIds.length === 0 || queueFilter === qid;
            })
            .map(conv => {
              const pill = STATUS_PILL[conv.status] || STATUS_PILL.CLOSED;
              return (
                <div key={conv.id} onClick={() => openConversation(conv)}
                  style={{ padding: '12px 14px', borderBottom: '1px solid #1e293b', cursor: 'pointer',
                    background: selected?.id === conv.id ? '#1e293b' : 'transparent',
                    transition: 'background 0.1s' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                    <div style={{ fontWeight: 600, fontSize: 13, color: '#f1f5f9' }}>
                      {conv.customer_name || conv.customer_id}
                    </div>
                    <span style={{ fontSize: 10, background: pill.bg, color: pill.text, borderRadius: 4, padding: '2px 6px', fontWeight: 700 }}>
                      {pill.label}
                    </span>
                  </div>
                  <div style={{ fontSize: 11, color: '#64748b' }}>
                    {CHANNEL_ICON[conv.channel] || '💬'} {conv.channel} · {new Date(conv.created_at).toLocaleTimeString()}
                  </div>
                  {conv.metadata?.target_queue_name && (
                    <div style={{ marginTop: 3 }}>
                      <span style={{ fontSize: 10, background: '#0ea5e922', color: '#0ea5e9', borderRadius: 4, padding: '2px 6px', fontWeight: 700 }}>
                        📋 {conv.metadata.target_queue_name}
                      </span>
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>
      </div>

      {/* ── Right panel: chat ── */}
      {selected ? (
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>

          {/* Chat header */}
          <div style={{ padding: '12px 18px', borderBottom: '1px solid #1e293b', display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontWeight: 700, fontSize: 15, color: '#f1f5f9', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {CHANNEL_ICON[selected.channel] || '💬'} {selected.customer_name || selected.customer_id}
              </div>
              <div style={{ fontSize: 11, color: '#64748b' }}>
                {selected.channel} · {selected.id.slice(0, 8)}… · {(STATUS_PILL[selected.status] || STATUS_PILL.CLOSED).label}
              </div>
            </div>

            {/* Action buttons */}
            {selected.status === 'WAITING_AGENT' && (
              <button onClick={() => takeover(selected)} style={btnGreen}>✋ Take Over</button>
            )}
            {selected.status === 'AGENT_ASSIGNED' && (
              <>
                <button onClick={getAiSuggestion} disabled={loadingSuggest} style={btnPurple}>
                  {loadingSuggest ? '🤔…' : '✨ AI Suggest'}
                </button>
                <button onClick={release} style={btnAmber}>🤖 Release to AI</button>
                <button onClick={closeConv} style={btnRed}>✓ Close</button>
              </>
            )}
          </div>

          {/* AI suggestion strip */}
          {aiSuggest && (
            <div style={{ margin: '8px 14px 0', background: '#4c1d9511', border: '1px solid #7c3aed44', borderRadius: 10, padding: '10px 14px' }}>
              <div style={{ fontSize: 11, color: '#a78bfa', fontWeight: 700, marginBottom: 6 }}>✨ AI Suggested Reply</div>
              <div style={{ fontSize: 13, color: '#ddd6fe', lineHeight: 1.5 }}>{aiSuggest}</div>
              <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                <button onClick={() => { setInput(aiSuggest); setAiSuggest(''); }} style={{ ...btnPurple, fontSize: 11, padding: '4px 10px' }}>
                  Use This
                </button>
                <button onClick={() => setAiSuggest('')} style={{ ...btnGhost, fontSize: 11, padding: '4px 10px' }}>
                  Dismiss
                </button>
              </div>
            </div>
          )}

          {/* Error banner */}
          {actionError && (
            <div style={{ margin: '8px 14px 0', background: '#ef444422', border: '1px solid #ef444466', borderRadius: 8, padding: '8px 14px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontSize: 13, color: '#fca5a5' }}>⚠ {actionError}</span>
              <button onClick={() => setActionError('')} style={{ background: 'none', border: 'none', color: '#f87171', cursor: 'pointer', fontSize: 16, lineHeight: 1 }}>×</button>
            </div>
          )}

          {/* Messages */}
          <div style={{ flex: 1, overflowY: 'auto', padding: '16px 14px', display: 'flex', flexDirection: 'column', gap: 10 }}>
            {messages.map((m, i) => {
              const b = BUBBLE[m.sender_type] || BUBBLE.SYSTEM;
              const isSystem = m.sender_type === 'SYSTEM';
              return (
                <div key={m.id || i} style={{ display: 'flex', flexDirection: 'column', alignItems: b.align }}>
                  {/* Sender label */}
                  <div style={{ fontSize: 10, color: '#64748b', marginBottom: 3, display: 'flex', gap: 8, alignItems: 'center' }}>
                    <span style={{ fontWeight: 600 }}>
                      {m.sender_type === 'CUSTOMER' ? (selected.customer_name || selected.customer_id) :
                       m.sender_type === 'AI' ? '🤖 AI Assistant' :
                       m.sender_type === 'AGENT' ? '👤 Agent' : '⚙ System'}
                    </span>
                    <span>{new Date(m.timestamp).toLocaleTimeString()}</span>
                    {m.confidence_score !== undefined && (
                      <span style={{ color: m.confidence_score < 0.5 ? '#f59e0b' : '#10b981' }}>
                        {(m.confidence_score * 100).toFixed(0)}% conf
                      </span>
                    )}
                    {m.escalation_triggered && <span style={{ color: '#f59e0b' }}>⚠ escalated</span>}
                  </div>

                  {/* Bubble */}
                  <div style={{
                    background: b.bg, color: b.color, borderRadius: 12, padding: isSystem ? '6px 12px' : '10px 14px',
                    fontSize: isSystem ? 12 : 14, lineHeight: 1.55,
                    maxWidth: isSystem ? '100%' : '75%',
                    fontStyle: isSystem ? 'italic' : 'normal',
                    border: isSystem ? '1px dashed #92400e44' : 'none',
                    width: isSystem ? '100%' : undefined,
                    textAlign: isSystem ? 'center' : 'left',
                  }}>
                    {m.message}
                  </div>
                </div>
              );
            })}

            {typing && (
              <div style={{ alignSelf: 'flex-end', fontSize: 12, color: '#64748b', fontStyle: 'italic' }}>
                Customer is typing…
              </div>
            )}
            <div ref={bottomRef} />
          </div>

          {/* Input area — only shown when agent has taken over */}
          {selected.status === 'AGENT_ASSIGNED' ? (
            <div style={{ padding: '12px 14px', borderTop: '1px solid #1e293b', display: 'flex', gap: 10, alignItems: 'flex-end' }}>
              <textarea
                value={input}
                onChange={e => {
                  setInput(e.target.value);
                  // Debounced typing indicator — send at most every 2s
                  if (!typingTimerRef.current && convWsRef.current) {
                    convWsRef.current.send({ type: 'typing' });
                    typingTimerRef.current = setTimeout(() => { typingTimerRef.current = null; }, 2000);
                  }
                }}
                onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMessage(); } }}
                placeholder="Type a reply… (Enter to send, Shift+Enter for new line)"
                rows={2}
                style={{ flex: 1, background: '#1e293b', border: '1px solid #334155', color: '#e2e8f0', borderRadius: 10, padding: '10px 14px', fontSize: 14, resize: 'none', lineHeight: 1.5, fontFamily: 'inherit' }}
              />
              <button onClick={sendMessage} disabled={sending || !input.trim()}
                style={{ ...btnGreen, padding: '10px 18px', fontSize: 14, alignSelf: 'stretch' }}>
                {sending ? '…' : '↑ Send'}
              </button>
            </div>
          ) : (
            <div style={{ padding: '12px 14px', borderTop: '1px solid #1e293b', background: '#1e293b33' }}>
              {selected.status === 'WAITING_AGENT' ? (
                <div style={{ textAlign: 'center', fontSize: 13, color: '#f59e0b' }}>
                  ⚠ This conversation is waiting for an agent. Click <strong>Take Over</strong> to start chatting.
                </div>
              ) : selected.status === 'AI_ACTIVE' ? (
                <div style={{ textAlign: 'center', fontSize: 13, color: '#64748b' }}>
                  🤖 AI is handling this conversation. View only.
                </div>
              ) : null}
            </div>
          )}
        </div>
      ) : (
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', color: '#334155', gap: 12 }}>
          <div style={{ fontSize: 48 }}>🤖</div>
          <div style={{ fontSize: 16, fontWeight: 600, color: '#475569' }}>AI Inbox</div>
          <div style={{ fontSize: 13, color: '#334155', textAlign: 'center', maxWidth: 260, lineHeight: 1.6 }}>
            {waiting.length > 0
              ? `${waiting.length} conversation${waiting.length > 1 ? 's' : ''} waiting for an agent. Select one to take over.`
              : 'All conversations are being handled by the AI. New escalations will appear here instantly.'}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Button styles ─────────────────────────────────────────────────────────────
const base: React.CSSProperties = { border: 'none', borderRadius: 8, padding: '7px 14px', fontSize: 12, fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap', transition: 'opacity 0.15s' };
const btnGreen:  React.CSSProperties = { ...base, background: '#10b981', color: '#fff' };
const btnPurple: React.CSSProperties = { ...base, background: '#7c3aed', color: '#fff' };
const btnAmber:  React.CSSProperties = { ...base, background: '#f59e0b', color: '#000' };
const btnRed:    React.CSSProperties = { ...base, background: '#ef4444', color: '#fff' };
const btnGhost:  React.CSSProperties = { ...base, background: 'transparent', color: '#94a3b8', border: '1px solid #334155' };
